<script setup>
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  getPermissionTokensForAccount,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';

const route = useRoute();
const router = useRouter();
const store = useStore();
const { shouldShow } = usePolicy();
const ariaLabel = 'Relatórios';

const tabs = [
  {
    key: 'relatorio',
    label: 'Relatório',
    icon: 'i-lucide-bar-chart-3',
    routeName: 'relatorio_index',
    activeOn: ['relatorio_index', 'desempenho_equipe_index', 'metas_index'],
    permissions: ['administrator', 'agent'],
  },
  {
    key: 'historico-operacional',
    label: 'Histórico operacional',
    icon: 'i-lucide-history',
    routeName: 'operational_history_index',
    activeOn: ['operational_history_index'],
    permissions: ['administrator', 'agent'],
  },
  {
    key: 'visao-geral',
    label: 'Visão geral',
    icon: 'i-lucide-chart-no-axes-combined',
    routeName: 'account_overview_reports',
    activeOn: ['account_overview_reports'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'conversas',
    label: 'Conversas',
    icon: 'i-lucide-message-square-text',
    routeName: 'conversation_reports',
    activeOn: ['conversation_reports'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'agentes',
    label: 'Agentes',
    icon: 'i-lucide-user-round-search',
    routeName: 'agent_reports_index',
    policyRouteName: 'account_overview_reports',
    activeOn: ['agent_reports', 'agent_reports_index', 'agent_reports_show'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'etiquetas',
    label: 'Etiquetas',
    icon: 'i-lucide-tags',
    routeName: 'label_reports_index',
    policyRouteName: 'account_overview_reports',
    activeOn: ['label_reports', 'label_reports_index', 'label_reports_show'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'caixas-de-entrada',
    label: 'Caixas de entrada',
    icon: 'i-lucide-inbox',
    routeName: 'inbox_reports_index',
    policyRouteName: 'account_overview_reports',
    activeOn: ['inbox_reports', 'inbox_reports_index', 'inbox_reports_show'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'equipes',
    label: 'Equipes',
    icon: 'i-lucide-users-round',
    routeName: 'team_reports_index',
    policyRouteName: 'account_overview_reports',
    activeOn: ['team_reports', 'team_reports_index', 'team_reports_show'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'csat',
    label: 'CSAT',
    icon: 'i-lucide-smile',
    routeName: 'csat_reports',
    activeOn: ['csat_reports'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'sla',
    label: 'SLA',
    icon: 'i-lucide-clock-alert',
    routeName: 'sla_reports',
    activeOn: ['sla_reports'],
    permissions: ['administrator', 'report_manage'],
  },
  {
    key: 'bots',
    label: 'Bots',
    icon: 'i-lucide-bot',
    routeName: 'bot_reports',
    activeOn: ['bot_reports'],
    permissions: ['administrator', 'report_manage'],
  },
];

const currentPermissions = computed(() =>
  getPermissionTokensForAccount(store.getters.getCurrentAccount || {})
);

const visibleTabs = computed(() =>
  tabs.filter(tab => {
    if (!hasPermissions(tab.permissions, currentPermissions.value)) {
      return false;
    }

    const routeRecord = router
      .getRoutes()
      .find(
        candidate => candidate.name === (tab.policyRouteName || tab.routeName)
      );

    if (!routeRecord) return false;

    return shouldShow(
      routeRecord.meta?.featureFlag,
      routeRecord.meta?.permissions,
      routeRecord.meta?.installationTypes
    );
  })
);

const isActive = tab =>
  [tab.routeName, ...(tab.activeOn || [])].includes(route.name);

const tabTarget = tab => ({
  name: tab.routeName,
  params: { accountId: route.params.accountId },
});
</script>

<template>
  <nav
    :aria-label="ariaLabel"
    class="border-b border-lais-border-rule bg-white px-4 sm:px-6 lg:px-10"
  >
    <div class="flex items-center gap-6 overflow-x-auto no-scrollbar">
      <RouterLink
        v-for="tab in visibleTabs"
        :key="tab.key"
        :to="tabTarget(tab)"
        class="relative inline-flex h-12 shrink-0 items-center gap-2 text-[14px] font-semibold transition-colors"
        :class="
          isActive(tab)
            ? 'text-lais-primary'
            : 'text-lais-text-muted hover:text-lais-text-body'
        "
      >
        <span class="size-4 shrink-0" :class="tab.icon" aria-hidden="true" />
        <span>{{ tab.label }}</span>
        <span
          v-if="isActive(tab)"
          class="absolute bottom-0 left-0 right-0 h-[3px] rounded-t bg-lais-primary"
        />
      </RouterLink>
    </div>
  </nav>
</template>
